/**
 * React Query hooks for Video Calls
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { videocallsService } from '@/api/services/videocalls.service';
import type { StartMeetingRequest } from '@/api/types/videocall.types';
import { message } from 'antd';

export const VIDEOCALLS_QUERY_KEY = 'videocalls';

/**
 * Hook for starting a video call
 */
export const useStartVideoCall = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: StartMeetingRequest) =>
      videocallsService.startMeeting(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [VIDEOCALLS_QUERY_KEY] });
      message.success('Video call started!');
    },
    onError: (error: Error) => {
      message.error(error?.message || 'Failed to start video call');
    },
  });
};

/**
 * Hook for ending a video call
 */
export const useEndVideoCall = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (meetingId: string) => videocallsService.endMeeting(meetingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [VIDEOCALLS_QUERY_KEY] });
    },
  });
};

/**
 * Hook for fetching active meetings
 */
export const useActiveMeetings = () => {
  return useQuery({
    queryKey: [VIDEOCALLS_QUERY_KEY, 'active'],
    queryFn: () => videocallsService.getActiveMeetings(),
    refetchInterval: 30000, // Refetch every 30 seconds
    staleTime: 20000, // Consider data stale after 20 seconds
  });
};

/**
 * Hook for fetching a specific meeting
 */
export const useVideoCallMeeting = (meetingId: string | null) => {
  return useQuery({
    queryKey: [VIDEOCALLS_QUERY_KEY, meetingId],
    queryFn: () => videocallsService.getMeeting(meetingId!),
    enabled: !!meetingId,
  });
};
